import { Injectable } from '@angular/core';
import { AbstractSecurityStorage } from 'angular-auth-oidc-client';

/**
 * Stores angular-auth-oidc-client state in localStorage instead of the
 * default sessionStorage, so opening em3.admin in a new tab does not force
 * the user back through the Keycloak login (sessionStorage is per-tab;
 * localStorage is shared across same-origin tabs).
 *
 * IMPORTANT: angular-auth-oidc-client v15's default DefaultLocalStorageService
 * is a pure pass-through -- it does NOT JSON.stringify on write or
 * JSON.parse on read. The library does its own serialization/deserialization
 * before/after calling the storage. Our implementation MUST match that
 * contract or values get double-encoded.
 *
 * History of bugs in this file:
 *   - PR #675 (2026-06-04): added JSON.stringify on write and JSON.parse on
 *     read, thinking the library wanted opaque-object semantics. That
 *     double-encoded every value (library JSON.stringify before passing to
 *     us, we JSON.stringify again). After a refresh, read returned a string
 *     instead of an object, and the library treated it as no auth state --
 *     calls to forceRefreshSession returned isAuthenticated:false with no
 *     network attempt.
 *   - PR #683 (2026-06-05): added defensive JSON.parse-failure handling
 *     and null/undefined removeItem semantics. Made the double-encoding bug
 *     harder to catch because reads succeeded (returning the wrong shape)
 *     and undefined writes silently removed keys instead of erroring.
 *   - This file (2026-06-06): match the library default. Pure passthrough.
 *     Values written by the library land in localStorage as-is; reads return
 *     the raw string. The library handles JSON itself.
 *
 * Trade-off: localStorage persists across browser sessions and is readable
 * by any script on the origin. em3.admin runs minified production builds
 * with Sentry-uploaded source maps and is restricted to known staff -- the
 * marginal XSS surface delta vs sessionStorage is acceptable.
 */
@Injectable({ providedIn: 'root' })
export class OidcLocalStorageService implements AbstractSecurityStorage {

    read(key: string): any {
        return localStorage.getItem(key);
    }

    write(key: string, value: any): void {
        try {
            localStorage.setItem(key, value);
        } catch (err) {
            // localStorage quota exceeded or storage disabled. Log and move
            // on so the auth flow can fall back to in-memory state rather
            // than throwing into the OIDC library.
            // eslint-disable-next-line no-console
            console.warn('OidcLocalStorageService.write failed:', err);
        }
    }

    remove(key: string): void {
        localStorage.removeItem(key);
    }

    clear(): void {
        // The library calls clear() during logout. We intentionally only
        // remove the OIDC-managed keys rather than nuking all of localStorage
        // -- em3.admin uses ngrx-store-localstorage for NgRx state persistence
        // and header-nav stores UI prefs in localStorage too; those belong to
        // the user across sessions, not the auth flow.
        const prefixes = ['em3-admin', '0-em3-admin', '0.em3-admin', 'oidc.'];
        const wellKnown = new Set([
            'authnResult', 'authzData', 'session_state', 'authStateControl',
            'authNonce', 'storage_codeVerifier', 'access_token', 'refresh_token',
            'id_token', 'silent_renew_running', 'codeFlowInProgress',
        ]);
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key === null) continue;
            if (prefixes.some(p => key.startsWith(p)) || wellKnown.has(key)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(k => localStorage.removeItem(k));
    }
}
